import React, { useContext, useEffect, useRef, useState } from "react";
import { GET } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import AuthContext from "../../store/auth-context";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, Info } from "lucide-react";
import CfLoader from "../../common/CfLoader/CfLoader";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import ProgrammeBatchCard from "../../components/Cards/ProgrammeBatchCard/ProgrammeBatchCard";
import FilterIcon from "../../resources/icons/filter.svg";
import PlusCircle from "../../resources/icons/plus-circle.svg";
import AddTemplateModal from "../../common/AddTemplateModal/AddTemplateModal";
import useNavigationStore from "../../store/navigationStore";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import {
  GetProgrammeName,
  GetBatchName,
} from "../../common/GetProgrammeAndBatch/GetProgrammeAndBatch";

const BatchIssuancePage = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const [isLoadingCredTemplates, setIsLoadingCredTemplates] = useState(false);
  const [cardWidth] = useState("19.125rem");
  const [gap] = useState("1.5rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const [countRecentList, setCountRecentList] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [recentList, setRecentList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [templateType, setTemplateType] = useState("All Templates");
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [actualProgrammeName, setActualProgrammeName] = useState("");
  const [actualBatchName, setActualBatchName] = useState("");

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      console.warn(
        "Programme or Batch context missing. Redirecting to workspace."
      );
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, navigate]);

  const fetchProgrammeName = () => {
    GET(EntityConstants.GET_PROGRAMME_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.programme_display_name) {
          setProgrammeName(data.programme_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching programme name:", error);
      });
  };

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  useEffect(() => {
    fetchProgrammeName();
    fetchBatchName();
  }, []);

  useEffect(() => {
    const fetchNames = async () => {
      const programmeParam = `programme_code=${
        currentProgramme?.programme_code || ""
      }`;
      const batchParam = `batch_code=${currentBatch?.batch_code || ""}`;

      const programmeName = await GetProgrammeName(programmeParam);
      const batchName = await GetBatchName(batchParam);

      setActualProgrammeName(programmeName.programme_name || "");
      setActualBatchName(batchName.batch_name || "");
    };

    if (currentProgramme?.programme_code && currentBatch?.batch_code) {
      fetchNames();
    }
  }, [currentProgramme, currentBatch]);

  const GET_ISSUER_RECENT = (limit, offset) => {
    if (!currentBatch) {
      setIsLoadingCredTemplates(false);
      return;
    }
    setIsLoadingCredTemplates(true);
    const apiUrl =
      templateType === "All Templates"
        ? `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&limit=${limit}&offset=${offset}`
        : `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&issuer_template_type=${templateType}&limit=${limit}&offset=${offset}`;

    GET(apiUrl)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.log(data.error);
        } else {
          setNext(data.data.next);
          setPrevious(data.data.previous);
          setCountRecentList(data.data.count);
          setRecentList(data.data.results);
          authContext.DraftOffsetHandler(offset);
        }
      })
      .then(() => setIsLoadingCredTemplates(false))
      .catch((error) => {
        console.error(error);
      })
      .finally(() => setIsLoadingCredTemplates(false));
  };

  const performSearchAPI = async (searchTerm) => {
    try {
      if (!currentBatch) {
        console.warn("Batch context missing during search.");
        setSearchResults([]);
        setSearchCompleted(true);
        setIsSearching(false);
        return;
      }

      const apiUrl =
        templateType === "All Templates"
          ? `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&limit=${cardsPerRow}&offset=0&search=${searchTerm}`
          : `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&issuer_template_type=${templateType}&limit=${cardsPerRow}&offset=0&search=${searchTerm}`;

      const response = await GET(apiUrl);
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        setSearchResults([]);
      } else {
        setSearchResults(data.data.results);
        setCountRecentList(data.data.count);
        setNext(data.data.next);
        setPrevious(data.data.previous);
      }
      setSearchCompleted(true);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error during search:", error);
        setSearchResults([]);
      }
      setSearchCompleted(true);
    } finally {
      setIsSearching(false);
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const handleSearchInputChange = (e) => {
    const term = e.target.value ? e.target.value : "";
    setSearchTerm(term);

    if (term === "") {
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(false);
      return;
    }

    if (!currentBatch) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(true);
      console.warn("Batch context missing during search.");
      return;
    }

    setSearchResults([]);
    setIsSearching(true);
    setSearchCompleted(false);
    handleSearch(term, {
      onStart: () => setIsSearching(true),
      onComplete: () => setIsSearching(false),
      onError: () => {
        setSearchResults([]);
        setSearchCompleted(true);
        setIsSearching(false);
      },
      minChars: 1,
    });
  };

  useEffect(() => {
    if (cardsPerRow > 0 && currentBatch) {
      GET_ISSUER_RECENT(cardsPerRow, 0);
    }
  }, [templateType, cardsPerRow, refreshKey, currentBatch]);

  useEffect(() => {
    if (searchTerm !== "" && currentBatch) {
      setSearchResults([]);
      setIsSearching(true);
      setSearchCompleted(false);
      performSearchAPI(searchTerm);
    }
  }, [templateType, searchTerm, currentBatch]);
  
  const handlePrevious = () => {
    const newOffset = authContext.draftOffset - cardsPerRow;
    if (searchTerm) {
      const apiUrl =
        templateType === "All Templates"
          ? `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&limit=${cardsPerRow}&offset=${newOffset}`
          : `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&issuer_template_type=${templateType}&limit=${cardsPerRow}&offset=${newOffset}`;

      GET(apiUrl)
        .then((response) => response.json())
        .then((data) => {
          setSearchResults(data.data.results);
          setNext(data.data.next);
          setPrevious(data.data.previous);
          authContext.DraftOffsetHandler(newOffset);
        })
        .catch((error) =>
          console.error("Error fetching previous search results:", error)
        );
    } else {
      GET_ISSUER_RECENT(cardsPerRow, newOffset);
    }
  };

  const handleNext = () => {
    const newOffset = authContext.draftOffset + cardsPerRow;
    if (searchTerm) {
      const apiUrl =
        templateType === "All Templates"
          ? `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&limit=${cardsPerRow}&offset=${newOffset}`
          : `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?batch_code=${currentBatch.batch_code}&issuer_template_type=${templateType}&limit=${cardsPerRow}&offset=${newOffset}`;
      GET(apiUrl)
        .then((response) => response.json())
        .then((data) => {
          setSearchResults(data.data.results);
          setNext(data.data.next);
          setPrevious(data.data.previous);
          authContext.DraftOffsetHandler(newOffset);
        })
        .catch((error) =>
          console.error("Error fetching next search results:", error)
        );
    } else {
      GET_ISSUER_RECENT(cardsPerRow, newOffset);
    }
  };

  const refreshTemplates = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const removeCredentialFromList = (credentialId) => {
    const removeAndEnsure = (list, setList, startDay, endDay, count) => {
      setList((prev) => {
        const updatedList = prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId
        );
        if (currentBatch) {
          ensureFullRow(updatedList, setList, startDay, endDay, count);
        }
        return updatedList;
      });
    };

    const ensureFullRow = (list, setList, count) => {
      if (list.length < cardsPerRow && count > list.length && currentBatch) {
        const offset = list.length;
        GET(
          `${
            EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES
          }?batch_code=${currentBatch.batch_code}&limit=${
            cardsPerRow - list.length
          }&offset=${offset}`
        )
          .then((response) => response.json())
          .then((data) => {
            if (!data.error && data.data.results.length > 0) {
              setList((prev) => [...prev, ...data.data.results]);
            }
          })
          .catch((error) => {
            console.error("Error fetching additional certificates:", error);
          });
      }
    };
    removeAndEnsure(searchResults, setSearchResults, countRecentList);
    removeAndEnsure(recentList, setRecentList, countRecentList);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace`);
  };

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap
        );
        if (calculatedCardsPerRow < 5) {
          setCardsPerRow(6);
        } else {
          setCardsPerRow(calculatedCardsPerRow);
        }
      }
    };

    updateCardsPerRow();
    window.addEventListener("resize", updateCardsPerRow);
    return () => window.removeEventListener("resize", updateCardsPerRow);
  }, [cardWidth, gap]);

  useEffect(() => {
    if (cardsPerRow > 0 && currentBatch) {
      GET_ISSUER_RECENT(cardsPerRow, 0);
    }
  }, [cardsPerRow, currentBatch]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, [location.pathname, authContext]);

  if (!currentProgramme || !currentBatch) {
    return (
      <div className="w-full mt-20 bg-cfSecondary min-h-screen flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="w-full mt-20 bg-cfSecondary min-h-screen">
      <div className="">
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
        >
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-2 sm:gap-0`}
          >
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <div
                className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
              >
                <div className="">
                  <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                    <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <button
                          onClick={handleProgrammeClick}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleProgrammeClick();
                            }
                          }}
                          className="hover:text-gray-800 cursor-pointer"
                        >
                          {currentProgramme.programme_name || "Programme"}
                        </button>
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                        <button
                          onClick={handleBatchClick}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleBatchClick();
                            }
                          }}
                          className="hover:text-gray-800 cursor-pointer focus:outline-none"
                        >
                          {currentBatch.batch_name || "Batch"}
                        </button>
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-800 font-medium">
                          Certificates
                        </span>
                      </div>
                    </h2>
                  </div>
                </div>
              </div>
              <div className="border border-cfSignInSignUpInputBorder px-2 py-1 rounded-md bg-cfSecondary flex items-center gap-2">
                <img src={FilterIcon} alt="" className="w-4 h-4" />
                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  className="px-3 py-1 w-[10rem] border-none rounded-md text-sm focus:outline-none"
                >
                  <option value="All Templates">All Certificates</option>
                  <option value="SVG">SVG</option>
                  <option value="Legacy">Legacy</option>
                </select>
              </div>
            </div>
            {isModalOpen && (
              <AddTemplateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                programmeId={currentProgramme.programme_code}
                batchId={currentBatch.batch_code}
                onTemplateAdded={refreshTemplates}
              />
            )}
            <div className="flex flex-col sm:flex-row items-start sm:mt-0 gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                className="h-[2.75rem] min-w-[11rem] gap-2 bg-cfPrimaryButton text-cfSecondary rounded-[8px] flex items-center justify-center hover:bg-cfPrimaryDark transition duration-200 border border-gray-800"
              >
                <img src={PlusCircle} className="h-4 w-4" alt="" />
                <span className="font-medium">Add Certificate</span>
              </button>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem]"
              >
                <input
                  type="text"
                  name="search"
                  id="search"
                  onChange={handleSearchInputChange}
                  placeholder="Search certificates"
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  value={searchTerm}
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="w-full px-4 pb-4">
        {searchTerm !== "" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-[1rem]">
            {searchResults.map(
              ({
                issuer_credential_template_id,
                credential_template_id,
                certificate_name,
                preview_image,
                created_at,
                updated_at,
                description,
                tagsList,
                template_type,
                thumbnail_webp_url,
              }) => (
                <ProgrammeBatchCard
                  key={issuer_credential_template_id}
                  name={certificate_name}
                  credential_template_id={credential_template_id}
                  issuer_credential_template_id={issuer_credential_template_id}
                  created_at={created_at}
                  lastEdited={updated_at}
                  thumbnail_image={preview_image}
                  description={description}
                  tagsList={tagsList}
                  template_type={template_type}
                  navigate_url={`/issuer/workspace/batches/summary/${credential_template_id}/${issuer_credential_template_id}?templateType=${template_type}`}
                  removeCredentialFromList={removeCredentialFromList}
                  thumbnail_webp_url={thumbnail_webp_url}
                  programmeCode={currentProgramme.programme_code}
                  batchCode={currentBatch.batch_code}
                />
              )
            )}
            {isSearching && searchResults.length === 0 && (
              <div className="col-span-full w-full flex justify-center items-center py-4">
                <CfLoader />
              </div>
            )}
            {!isSearching && searchCompleted && searchResults.length === 0 && (
              <div className="col-span-full mt-6 w-full flex justify-center">
                <p className="text-lg font-semibold text-cfRootBgColor">
                  Certificate not found
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {isLoadingCredTemplates ? (
              <div className="w-full flex justify-center items-center py-12">
                <CfLoader />
              </div>
            ) : (
              <div>
                <div className="p-3 bg-blue-50 border-none rounded-lg my-3">
                  <div className="flex items-center">
                    <div className="mr-3 text-[#3538CD]">
                      <Info className="h-5 w-5" />
                    </div>
                    <p className="text-sm text-[#3538CD]">
                      Viewing certificates for{" "}
                      <span className="font-semibold">
                        {actualProgrammeName}
                      </span>{" "}
                      {programmeName}{" "}
                      <span className="font-semibold">{actualBatchName}</span>{" "}
                      {batchName}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-[1rem]">
                  {recentList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      preview_image,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      thumbnail_webp_url,
                    }) => (
                      <ProgrammeBatchCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={issuer_credential_template_id}
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_image={preview_image}
                        description={description}
                        tagsList={tagsList}
                        template_type={template_type}
                        navigate_url={`/issuer/workspace/batches/summary/${credential_template_id}/${issuer_credential_template_id}?templateType=${template_type}`}
                        removeCredentialFromList={removeCredentialFromList}
                        programmeCode={currentProgramme.programme_code}
                        batchCode={currentBatch.batch_code}
                        thumbnail_webp_url={thumbnail_webp_url}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            {recentList.length === 0 && !isLoadingCredTemplates && (
              <div className="mt-6 w-full flex justify-center">
                <p className="text-lg font-semibold text-cfRootBgColor">
                  No certificates found
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mt-6 w-full pb-8">
              <button
                onClick={handlePrevious}
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={previous === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>previous</span>
                </span>
              </button>

              <button
                onClick={handleNext}
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={next === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>Next</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BatchIssuancePage;
